import React, { useState } from 'react';
import { CreditCard, Mail, Loader2, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPaymentsTab = ({ 
  password, 
  payments, 
  paymentsTotal, 
  fetchStats,
  formatDate 
}) => {
  const [paymentSearch, setPaymentSearch] = useState('');
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [sendingFreshLink, setSendingFreshLink] = useState(null);

  const fetchAudit = async (email) => {
    setLoadingAudit(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/audit/${encodeURIComponent(email)}?password=${password}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedAudit(data);
      }
    } catch (error) {
      console.error('Failed to fetch audit:', error);
      toast.error('Failed to load audit trail');
    } finally {
      setLoadingAudit(false);
    }
  };

  const revokeAccess = async (email) => {
    if (!window.confirm(`Are you sure you want to revoke ALL access for ${email}? They will be kicked out immediately.`)) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/revoke-access?password=${password}&email=${encodeURIComponent(email)}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchStats();
        setSelectedAudit(null);
      } else {
        toast.error('Failed to revoke access');
      }
    } catch (error) {
      console.error('Failed to revoke access:', error);
      toast.error('Failed to revoke access');
    }
  };

  const sendFreshLinkFromPayments = async (email) => {
    if (!window.confirm(`This will:\n\n1. Destroy ALL existing access for ${email}\n2. Send a fresh magic link to their email\n\nThe user will be kicked out immediately if watching.\n\nProceed?`)) {
      return;
    }
    
    setSendingFreshLink(email);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/send-fresh-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password,
          email
        })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(`Fresh magic link sent to ${email}`);
        fetchStats();
        setSelectedAudit(null);
      } else {
        toast.error(data.detail || data.message || 'Failed to send fresh link');
      }
    } catch (error) {
      console.error('Failed to send fresh link:', error);
      toast.error('Failed to send fresh link');
    } finally {
      setSendingFreshLink(null);
    }
  };

  const filteredPayments = (payments || []).filter(p => 
    !paymentSearch || p.email?.toLowerCase().includes(paymentSearch.toLowerCase())
  );

  return (
    <div className="admin-card full-width" data-testid="payments-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2><CreditCard className="h-5 w-5" /> All Payments ({paymentsTotal})</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            window.open(`${API_URL}/api/admin/payments/export?password=${password}`, '_blank');
          }}
          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          data-testid="export-report-btn"
        >
          <Mail className="h-4 w-4" />
          Export Report
        </Button>
      </div>
      
      {/* Search */}
      <div style={{ marginBottom: '16px' }}>
        <input
          type="text"
          placeholder="Search by email..."
          value={paymentSearch}
          onChange={(e) => setPaymentSearch(e.target.value)}
          className="admin-input"
          style={{ maxWidth: '300px' }}
          data-testid="payment-search-input"
        />
      </div>
      
      <p style={{ fontSize: '12px', color: '#7a9db5', marginBottom: '16px' }}>
        🔒 Email addresses are sensitive data. Click on a row to view full audit trail.
      </p>
      
      {payments.length > 0 ? (
        <div className="payments-list" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid rgba(255,255,255,0.2)' }}>
                <th style={{ padding: '12px 8px', textAlign: 'left', color: '#7a9db5' }}>Email</th>
                <th style={{ padding: '12px 8px', textAlign: 'left', color: '#7a9db5' }}>Access Status</th>
                <th style={{ padding: '12px 8px', textAlign: 'left', color: '#7a9db5' }}>Sessions</th>
                <th style={{ padding: '12px 8px', textAlign: 'left', color: '#7a9db5' }}>Fresh Links</th>
                <th style={{ padding: '12px 8px', textAlign: 'left', color: '#7a9db5' }}>Date</th>
                <th style={{ padding: '12px 8px', textAlign: 'left', color: '#7a9db5' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment) => (
                <tr 
                  key={`payment-${payment.transaction_id || payment.session_id || `${payment.email}-${payment.created_at}`}`} 
                  style={{ 
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    background: payment.fresh_links_sent > 3 ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                    cursor: 'pointer'
                  }}
                  onClick={() => fetchAudit(payment.email)}
                  data-testid={`payment-row-${payment.email}`}
                >
                  <td style={{ padding: '12px 8px', color: '#e8f4f8', fontFamily: 'monospace', fontSize: '13px' }}>
                    {payment.email}
                  </td>
                  <td style={{ padding: '12px 8px' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: payment.access_status === 'active' ? '#22c55e20' : 
                                 payment.access_status === 'expired' ? '#f59e0b20' :
                                 payment.access_status === 'used' ? '#3b82f620' : '#64748b20',
                      color: payment.access_status === 'active' ? '#22c55e' : 
                            payment.access_status === 'expired' ? '#f59e0b' :
                            payment.access_status === 'used' ? '#3b82f6' : '#64748b'
                    }}>
                      {payment.access_status === 'active' ? '🟢 ACTIVE' : 
                       payment.access_status === 'expired' ? '🟡 EXPIRED' :
                       payment.access_status === 'used' ? '✅ USED' : '⚪ NOT ACCESSED'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 8px', color: '#7a9db5' }}>
                    {payment.total_sessions || 1}
                  </td>
                  <td style={{ padding: '12px 8px' }}>
                    {payment.fresh_links_sent > 0 ? (
                      <span style={{ 
                        color: payment.fresh_links_sent > 3 ? '#ef4444' : payment.fresh_links_sent > 1 ? '#f59e0b' : '#7a9db5',
                        fontWeight: '600'
                      }}>
                        {payment.fresh_links_sent}x 
                        <span style={{ fontSize: '10px', opacity: 0.7 }}>
                          ({payment.fresh_links_tezza || 0} AI, {payment.fresh_links_admin || 0} Admin)
                        </span>
                      </span>
                    ) : (
                      <span style={{ color: '#22c55e' }}>Clean ✓</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 8px', color: '#7a9db5', fontSize: '13px' }}>
                    {formatDate(payment.completed_at || payment.created_at)}
                  </td>
                  <td style={{ padding: '12px 8px' }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => sendFreshLinkFromPayments(payment.email)}
                        disabled={sendingFreshLink === payment.email}
                        style={{ fontSize: '11px', padding: '4px 8px' }}
                        title="Revoke old links and send a fresh one"
                        data-testid={`fresh-link-btn-${payment.email}`}
                      >
                        {sendingFreshLink === payment.email ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3 w-3 mr-1" />
                        )}
                        Fresh Link
                      </Button>
                      {payment.access_status === 'active' && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => revokeAccess(payment.email)}
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          data-testid={`revoke-btn-${payment.email}`}
                        >
                          Revoke
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="admin-empty">
          <CreditCard className="h-12 w-12" />
          <p>No payments yet.</p>
        </div>
      )}
      
      {/* Audit Modal */}
      {selectedAudit && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setSelectedAudit(null)} data-testid="audit-modal">
          <div style={{
            background: '#1a1a2e',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#e8f4f8' }}>🔍 Audit Trail: {selectedAudit.email}</h3>
              <Button variant="outline" size="sm" onClick={() => setSelectedAudit(null)}>Close</Button>
            </div>
            
            {/* Summary */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
              <div style={{ background: '#0a0a0f', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#22c55e' }}>{selectedAudit.summary.total_payments}</div>
                <div style={{ fontSize: '11px', color: '#7a9db5' }}>Payments</div>
              </div>
              <div style={{ background: '#0a0a0f', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#3b82f6' }}>{selectedAudit.summary.total_sessions}</div>
                <div style={{ fontSize: '11px', color: '#7a9db5' }}>Sessions</div>
              </div>
              <div style={{ background: '#0a0a0f', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: selectedAudit.summary.active_sessions > 0 ? '#22c55e' : '#64748b' }}>
                  {selectedAudit.summary.active_sessions}
                </div>
                <div style={{ fontSize: '11px', color: '#7a9db5' }}>Active</div>
              </div>
              <div style={{ background: '#0a0a0f', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#f59e0b' }}>{selectedAudit.summary.tezza_interactions}</div>
                <div style={{ fontSize: '11px', color: '#7a9db5' }}>AI Helps</div>
              </div>
            </div>
            
            {/* Sessions */}
            <h4 style={{ color: '#e8f4f8', marginBottom: '12px' }}>Stream Sessions ({selectedAudit.sessions.length})</h4>
            <div style={{ background: '#0a0a0f', borderRadius: '8px', padding: '12px', marginBottom: '20px', maxHeight: '200px', overflow: 'auto' }}>
              {selectedAudit.sessions.map((session) => (
                <div key={`session-${session.created_at}-${session.device_fingerprint || 'unknown'}`} style={{ 
                  padding: '8px', 
                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                  fontSize: '12px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: session.is_active ? '#22c55e' : '#ef4444' }}>
                      {session.is_active ? '🟢 Active' : '🔴 Inactive'}
                    </span>
                    <span style={{ color: '#7a9db5' }}>{formatDate(session.created_at)}</span>
                  </div>
                  {session.deactivated_reason && (
                    <div style={{ color: '#f59e0b', fontSize: '11px', marginTop: '4px' }}>
                      Deactivated: {session.deactivated_reason}
                    </div>
                  )}
                  {session.device_fingerprint && (
                    <div style={{ color: '#64748b', fontSize: '10px', fontFamily: 'monospace', marginTop: '4px' }}>
                      Device: {session.device_fingerprint.substring(0, 20)}...
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Tezza Activity */}
            {selectedAudit.tezza_activity.length > 0 && (
              <>
                <h4 style={{ color: '#e8f4f8', marginBottom: '12px' }}>Tezza AI Interactions ({selectedAudit.tezza_activity.length})</h4>
                <div style={{ background: '#0a0a0f', borderRadius: '8px', padding: '12px', maxHeight: '150px', overflow: 'auto' }}>
                  {selectedAudit.tezza_activity.map((activity) => (
                    <div key={`tezza-${activity.created_at}-${activity.action}`} style={{ 
                      padding: '8px', 
                      borderBottom: '1px solid rgba(255,255,255,0.1)',
                      fontSize: '12px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: activity.success ? '#22c55e' : '#ef4444' }}>
                          {activity.action === 'send_fresh_link' ? '📧 Fresh Link Sent' : '🔍 Payment Check'}
                        </span>
                        <span style={{ color: '#7a9db5' }}>{formatDate(activity.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            
            {/* Actions */}
            <div style={{ marginTop: '20px', display: 'flex', gap: '12px' }}>
              <Button
                variant="outline"
                onClick={() => sendFreshLinkFromPayments(selectedAudit.email)}
                disabled={sendingFreshLink === selectedAudit.email}
              >
                {sendingFreshLink === selectedAudit.email ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Send Fresh Link
              </Button>
              {selectedAudit.summary.active_sessions > 0 && (
                <Button variant="destructive" onClick={() => revokeAccess(selectedAudit.email)}>
                  Revoke All Access
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPaymentsTab;
